import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  Calendar, Clock, MapPin, Users, ExternalLink, 
  ArrowRight, CheckCircle, Loader2, Share2, Copy,
  DollarSign, User, Mail, Sparkles
} from "lucide-react";

export const PublicEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ name: "", email: "" });

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (user && event) {
      checkRegistration();
    }
  }, [user, event]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API}/public/events/${eventId}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      } else {
        toast.error("Event not found");
      }
    } catch (_error) {
      toast.error("Failed to load event");
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/events/${eventId}/check-registration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRegistered(data.registered);
      }
    } catch (_error) {
      console.error("Failed to check registration");
    }
  };

  const handleRegister = async () => {
    if (!user) {
      // Redirect to register with return URL
      navigate(`/register?redirect=/event/${eventId}&join=community`);
      return;
    }

    setRegistering(true);
    try {
      const response = await fetch(`${API}/events/${eventId}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        setRegistered(true);
        toast.success("Successfully registered for the event!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to register");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setRegistering(false);
    }
  };

  const copyShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Event link copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "long", 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">Event Not Found</h2>
            <p className="text-neutral-500 mb-6">This event may have been removed or the link is invalid.</p>
            <Link to="/">
              <Button className="bg-[#39FF14] hover:bg-[#009900] text-white">
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const maxAttendees = event.max_attendees || 50;
  const registrationCount = event.registration_count || 0;
  const spotsLeft = maxAttendees - registrationCount;
  const isFull = spotsLeft <= 0;
  const isPaid = event.price > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white" data-testid="public-event-page">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-[#39FF14] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">MB</span>
            </div>
            <span className="font-semibold text-neutral-900">My Biz Consulting</span>
          </Link>
          <Button variant="outline" size="sm" onClick={copyShareLink}>
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Type Badge */}
            <div className="flex items-center gap-2">
              <Badge className="bg-[#39FF14]/10 text-[#39FF14] capitalize">
                {event.type || "Event"}
              </Badge>
              {isPaid && (
                <Badge className="bg-amber-100 text-amber-700">
                  <DollarSign className="w-3 h-3 mr-1" />
                  Paid Event
                </Badge>
              )}
              {isFull && (
                <Badge variant="destructive">Sold Out</Badge>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-neutral-900">
              {event.title}
            </h1>

            {/* Date/Time/Location */}
            <div className="flex flex-wrap gap-4 text-neutral-600">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-[#39FF14]" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-[#39FF14]" />
                <span>{formatTime(event.time)}</span>
                {event.duration && <span className="ml-1">({event.duration} min)</span>}
              </div>
              {event.location && (
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-[#39FF14]" />
                  <span>{event.location}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <Card className="border-neutral-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">About This Event</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-600 whitespace-pre-wrap">{event.description}</p>
                </CardContent>
              </Card>
            )}

            {/* What You'll Learn / Get */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-amber-500" />
                  What to Expect
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 text-[#39FF14] flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-600">Live interactive session with Q&A</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 text-[#39FF14] flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-600">Actionable strategies you can implement immediately</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 text-[#39FF14] flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-600">Access to our community of like-minded entrepreneurs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 text-[#39FF14] flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-600">Recording available for registered attendees</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Host Info */}
            <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#39FF14] flex items-center justify-center text-white font-bold text-xl">
                    JL
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">Jamilah Lawry</p>
                    <p className="text-sm text-neutral-500">Business Strategist & Consultant</p>
                    <p className="text-sm text-neutral-500">My Biz Consulting</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Registration Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <Card className="border-[#39FF14]/30 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  {/* Price */}
                  <div className="text-center">
                    {isPaid ? (
                      <>
                        <p className="text-4xl font-bold text-neutral-900">${event.price}</p>
                        <p className="text-sm text-neutral-500">per person</p>
                      </>
                    ) : (
                      <>
                        <p className="text-4xl font-bold text-[#39FF14]">FREE</p>
                        <p className="text-sm text-neutral-500">Community members only</p>
                      </>
                    )}
                  </div>

                  {/* Spots Left */}
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-neutral-500" />
                    {isFull ? (
                      <span className="text-red-500 font-medium">Event is full</span>
                    ) : (
                      <span className="text-neutral-600">
                        <strong className="text-neutral-900">{spotsLeft}</strong> spots remaining
                      </span>
                    )}
                  </div>

                  {/* Registration Button */}
                  {registered ? (
                    <div className="text-center space-y-3">
                      <div className="flex items-center justify-center text-[#39FF14]">
                        <CheckCircle className="w-6 h-6 mr-2" />
                        <span className="font-semibold">You're Registered!</span>
                      </div>
                      {event.meeting_link && (
                        <a href={event.meeting_link} target="_blank" rel="noopener noreferrer">
                          <Button className="w-full bg-[#39FF14] hover:bg-[#009900] text-white">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Join Event
                          </Button>
                        </a>
                      )}
                      <p className="text-xs text-neutral-500">
                        We'll send you a reminder before the event
                      </p>
                    </div>
                  ) : isFull ? (
                    <Button disabled className="w-full">
                      Event Full - Join Waitlist
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      {user ? (
                        <Button
                          onClick={handleRegister}
                          disabled={registering}
                          className="w-full bg-[#39FF14] hover:bg-[#009900] text-white"
                          size="lg"
                          data-testid="register-event-btn"
                        >
                          {registering ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <ArrowRight className="w-4 h-4 mr-2" />
                          )}
                          {isPaid ? "Register & Pay" : "Register Now"}
                        </Button>
                      ) : (
                        <>
                          <Button
                            onClick={() => navigate(`/register?redirect=/event/${eventId}&join=community`)}
                            className="w-full bg-[#39FF14] hover:bg-[#009900] text-white"
                            size="lg"
                            data-testid="join-register-btn"
                          >
                            <Users className="w-4 h-4 mr-2" />
                            Join Community to Register
                          </Button>
                          <p className="text-center text-sm text-neutral-500">
                            Already a member?{" "}
                            <Link to={`/login?redirect=/event/${eventId}`} className="text-[#39FF14] hover:underline">
                              Sign in
                            </Link>
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Why Join */}
                  {!user && (
                    <div className="border-t border-neutral-200 pt-4">
                      <p className="text-xs font-medium text-neutral-700 mb-2">
                        Why join our community?
                      </p>
                      <ul className="text-xs text-neutral-500 space-y-1">
                        <li className="flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1 text-[#39FF14]" />
                          Access to all free events
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1 text-[#39FF14]" />
                          Connect with entrepreneurs
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1 text-[#39FF14]" />
                          Exclusive resources & tips
                        </li>
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Share Card */}
              <Card className="mt-4 border-neutral-200">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-neutral-700 mb-3">Share this event</p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={window.location.href} 
                      readOnly 
                      className="text-xs"
                    />
                    <Button variant="outline" size="sm" onClick={copyShareLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-100 py-8 px-4 border-t border-neutral-200 mt-16">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-neutral-500">
            © 2026 My Biz Consulting. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicEventPage;
